import { Button, Card, CardContent, Stack, TextField } from '@mui/material';
import { addAnomalyPayload, AnomalyAPI, Nullable, RegionAPI } from '@shared';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { auditApi } from 'App';
import {
  AnomalyCard,
  Empty,
  Heading,
  SelectAnomaly,
  StartEndTime,
  useAnomaliesFormik,
} from './shared';
import { RejectedAnomaliesPropsType } from '../AnomaliesSidebar.types';

export function RejectedAnomalies(props: RejectedAnomaliesPropsType) {
  const {
    onSeekChange,
    anomalyTypes,
    rejectedRegions,
    refetch,
    auditUuid,
    disabled = false,
  } = props;

  const queryClient = useQueryClient();

  const [isShowAdding, setIsShowAdding] = useState(false);
  const [currentRegion, setCurrentRegion] = useState({ start: 0, end: 0, uuid: '' });
  const { mutate: addAnomaly } = useMutation({
    mutationFn: (payload: addAnomalyPayload) => auditApi.addAnomaly(auditUuid, payload),
    onSuccess: (data) => {
      queryClient.setQueryData(['anomalies', auditUuid], (oldData: Nullable<AnomalyAPI[]>) => {
        if (oldData) {
          return [data, ...oldData];
        } else {
          refetch();
          return [];
        }
      });
      queryClient.setQueryData(['rejected regions', auditUuid], (oldData: Nullable<RegionAPI[]>) =>
        oldData?.filter((region) => region.uuid !== data.region.uuid)
      );
      setIsShowAdding(false);
    },
  });

  const { values, handleChange, handleSubmit } = useAnomaliesFormik(addAnomaly, currentRegion);

  const handleOpen = (start: number, end: number, uuid: string) => {
    if (disabled) {
      return;
    }
    setIsShowAdding(true);
    setCurrentRegion({ start, end, uuid });
    onSeekChange(start, false);
  };

  if (isShowAdding) {
    return (
      <Card variant='outlined'>
        <CardContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          component='form'
          onSubmit={handleSubmit}
        >
          <SelectAnomaly anomalyTypes={anomalyTypes} id={values.id} handleChange={handleChange} />
          <StartEndTime
            start={currentRegion.start}
            end={currentRegion.end}
            onSeekChange={onSeekChange}
            isReplayButton={true}
          />
          <TextField
            fullWidth
            label='Comments'
            multiline
            rows={4}
            name='comment'
            value={values.comment}
            onChange={handleChange}
          />
          <Button type='submit' disabled={!values.comment} variant='contained'>
            Confirm
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Heading title={'rejected anomalies'} />
      {rejectedRegions?.length === 0 ? (
        <Empty whichAnomaly={'Rejected'} />
      ) : (
        <Stack gap={2}>
          {rejectedRegions?.map(({ suspectType, start, end, uuid }) => (
            <AnomalyCard
              key={uuid}
              title={suspectType}
              uuid={uuid}
              start={start}
              end={end}
              onSeekChange={onSeekChange}
              handleOpen={handleOpen}
              disabled={disabled}
            />
          ))}
        </Stack>
      )}
    </>
  );
}
